import { Button, HFlow, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Form, FormRenderProps, PecFormApi, resetAllFieldStates, RichTextField, SubmitButton } from 'components/form'
import { TipoAtestadoEnum } from 'graphql/types.generated'
import { useState } from 'react'
import { metaPath } from 'util/metaPath'

import { AtestadoAssinaturaDigitalView } from '../assinatura-digital/AtestadoAssinaturaDigitalView'
import { AtestadoConfirmarAssinaturaDigitalModal } from '../assinatura-digital/AtestadoConfirmarAssinaturaDigitalModal'
import { MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED } from '../assinatura-digital/model-atestadoAssinaturaDigital'
import { useAtestadoAssinaturaDigitalForm } from '../assinatura-digital/useAtestadoAssinaturaDigitalForm'
import AtestadoModel from '../components/model-atestado'
import { useAtestadoCancelarCadastro } from '../hooks/useAtestadoCancelarCadastro'
import { atestadoEmBrancoFormValidator } from './validator'

export interface AtestadoEmBrancoFormProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadaoId: ID
  dataAtendimento: Instant
  initialValues?: AtestadoModel
  isDirtyForm?(): void
  onSubmit(values: AtestadoModel): void
}

export const name = metaPath<AtestadoModel>()

export function AtestadoEmBrancoForm(props: Readonly<AtestadoEmBrancoFormProps>) {
  const { atendimentoId, prontuarioId, cidadaoId, dataAtendimento, initialValues, isDirtyForm, onSubmit } = props
  const alert = useAlert()

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isEmailCidadaoDirty, setIsEmailCidadaoDirty] = useState(false)
  const [isEmailCidadaoCanceled, setIsEmailCidadaoCanceled] = useState(false)

  const tipoAtestado = TipoAtestadoEnum.EM_BRANCO

  const handleSubmit = (values: AtestadoModel, formApi: PecFormApi) => {
    onSubmit({ ...values, tipo: tipoAtestado })
    setTimeout(formApi.reset)
    resetAllFieldStates(formApi)
    alert('success', 'Atestado salvo com sucesso.')
  }

  const {
    emailCidadao,
    handleSubmitWrapper,
    isAssinando,
    isModalOpen,
    onConfirm,
    onCancel,
    errorMessage,
    onAlertClose,
  } = useAtestadoAssinaturaDigitalForm({
    cidadaoId,
    atendimentoId,
    prontuarioId,
    tipoAtestado,
    dataAtendimento,
    onSubmit: handleSubmit,
  })

  const handleCancel = useAtestadoCancelarCadastro({
    isEmailCidadaoDirty,
    setIsEmailCidadaoCanceled,
  })

  const renderForm = (formProps: FormRenderProps<AtestadoModel>) => {
    return (
      <>
        <VFlow>
          <AtestadoAssinaturaDigitalView
            name={name.assinadoDigitalmente}
            cidadaoId={cidadaoId}
            cidadaoEmail={emailCidadao}
            onEmailCidadaoChange={setIsSubmitDisabled}
            hasCid10={!!formProps.values.cid10}
            onDirtyChange={setIsEmailCidadaoDirty}
            emailCanceled={isEmailCidadaoCanceled}
            onEmailCancel={setIsEmailCidadaoCanceled}
          />
          <RichTextField
            name={name.descricao}
            maxLength={4000}
            placeholder='Insira conteúdo do atestado'
            height={7}
            label='Conteúdo'
            required
            onChange={isDirtyForm}
            dataTestId='conteudoAtestadoTextField'
          />

          <HFlow justifyContent='flex-end'>
            <Button size='small' onClick={() => handleCancel(formProps.form)}>
              Cancelar
            </Button>
            <Tooltip text={isSubmitDisabled ? MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED : undefined}>
              <SubmitButton
                type='button'
                size='small'
                kind='primary'
                handleSubmit={formProps.handleSubmit}
                throttleSubmit
                disabled={isSubmitDisabled}
              >
                Salvar atestado
              </SubmitButton>
            </Tooltip>
          </HFlow>
        </VFlow>

        {isModalOpen && (
          <AtestadoConfirmarAssinaturaDigitalModal
            emailCidadao={emailCidadao}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isAssinando={isAssinando}
            errorMessage={errorMessage}
            onAlertClose={onAlertClose}
            tipoAtestado={tipoAtestado}
            descricao={formProps.values.descricao}
          />
        )}
      </>
    )
  }

  return (
    <Form<AtestadoModel>
      initialValues={initialValues}
      render={renderForm}
      onSubmit={handleSubmitWrapper}
      validate={atestadoEmBrancoFormValidator}
    />
  )
}
